'use client';

import { useEffectOnce } from '@carvana/merch-react-hooks';
import { datadogRum } from '@datadog/browser-rum';
import { useRouter } from 'next/navigation';
import { useEffect, useLayoutEffect } from 'react';
import BrowserCookies from '~/utilities/BrowserCookies';
import reloadWindow from '~/utilities/reloadWindow';
import scrollToTop from '~/utilities/scrollToTop';

// useLayoutEffect fires before the browser paints, so the router.refresh()
// dispatch starts as early as possible — before the user sees stale geo.
// The server-side fallback to useEffect is required because Next.js SSRs
// 'use client' components to HTML; effects never execute on the server either
// way, so this is purely to silence the React warning.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

type OwnProps = {
  serverZip: string;
};

/**
 * Detects geo staleness on back/forward navigation from two distinct causes:
 * the Next.js router serving a cached RSC (routercache), and the browser
 * restoring the whole page from bfcache (bfcache). In both cases, when the
 * server-rendered zip (baked into the cached payload) no longer matches the
 * current CVCurrentZip cookie, the user has changed their location since this
 * page was last rendered, and the page is refreshed so geo-dependent content
 * reflects the current location.
 */
export default function GeoStaleChecker({ serverZip }: OwnProps) {
  const router = useRouter();

  useIsomorphicLayoutEffect(() => {
    // Only act on back/forward navigation where useSyncUrl
    // embedded SRP state — a fresh server navigation won't
    // have this marker, so the zip mismatch is expected.
    if (!window.history.state?.srp) return;

    const clientZip = BrowserCookies.get('CVCurrentZip') ?? '';
    if (clientZip && clientZip !== serverZip) {
      datadogRum.addAction('geo-stale-refresh', {
        clientZip,
        serverZip,
        source: 'routercache'
      });
      router.refresh();
    }
  }, [router, serverZip]);

  useEffectOnce(() => {
    if (typeof window === 'undefined') return;

    function handlePageShow(event: PageTransitionEvent) {
      if (!event.persisted) return;

      const clientZip = BrowserCookies.get('CVCurrentZip') ?? '';
      if (clientZip && clientZip !== serverZip) {
        datadogRum.addAction('geo-stale-refresh', {
          clientZip,
          serverZip,
          source: 'bfcache'
        });
        scrollToTop();
        reloadWindow();
      }
    }

    window.addEventListener('pageshow', handlePageShow);

    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [serverZip]);

  return null;
}
